import { lazy, memo, Suspense } from "react";
import { Download, ExternalLink, Paperclip } from "lucide-react";
import type { MessageView } from "@shared/api";
import { cn } from "@web/lib/cn";
import { addressLabel, formatAddress, formatBytes, participantsLabel } from "@web/lib/addresses";
import { attachmentUrl } from "@web/lib/endpoints";
import { formatFull, formatListTime } from "@web/lib/time";
import { loadMailFrame } from "@web/screens";
import { linkify } from "./linkify";

// The sanitizer and frame are their own chunk (warmed on idle), out of the first paint.
const MailFrame = lazy(() => loadMailFrame().then((m) => ({ default: m.MailFrame })));

function PlainBody({ text }: { text: string }) {
  return (
    <pre className="font-sans text-[15px] leading-relaxed break-words whitespace-pre-wrap text-ink">
      {linkify(text).map((segment, i) =>
        segment.type === "link" ? (
          <a
            key={i}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-text underline underline-offset-2"
          >
            {segment.value}
          </a>
        ) : (
          <span key={i}>{segment.value}</span>
        ),
      )}
    </pre>
  );
}

function MessageBody({ message }: { message: MessageView }) {
  if (message.bodyHtml) {
    return (
      <Suspense fallback={<div aria-busy className="min-h-24" />}>
        <MailFrame html={message.bodyHtml} title={`Message from ${addressLabel(message.from)}`} />
      </Suspense>
    );
  }
  if (message.bodyText) return <PlainBody text={message.bodyText} />;
  if (!message.bodyAvailable) {
    return (
      <p className="text-sm text-muted">
        This message hasn’t been downloaded yet. It will appear after the next sync.
      </p>
    );
  }
  return <p className="text-sm text-muted">This message has no text.</p>;
}

function Attachments({ message }: { message: MessageView }) {
  const files = message.attachments.filter((a) => !a.inline || !a.mimeType.startsWith("image/"));
  if (!files.length) return null;
  return (
    <ul
      className="flex flex-wrap gap-2 border-t border-line px-4 py-3 md:px-5"
      aria-label="Attachments"
    >
      {files.map((a) => (
        <li
          key={a.partId}
          className="flex max-w-full items-center gap-2 rounded-lg border border-line bg-bg py-1.5 pr-1.5 pl-3 text-sm"
        >
          <Paperclip aria-hidden className="size-3.5 shrink-0 text-muted" />
          <span className="min-w-0 truncate" title={a.filename}>
            {a.filename || "Untitled attachment"}
          </span>
          <span className="shrink-0 text-xs text-muted">{formatBytes(a.size)}</span>
          <a
            href={attachmentUrl(message.key, a.partId, true)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${a.filename}`}
            title="Open"
            className="rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
          >
            <ExternalLink className="size-3.5" />
          </a>
          <a
            href={attachmentUrl(message.key, a.partId)}
            download={a.filename || true}
            aria-label={`Download ${a.filename}`}
            title="Download"
            className="rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
          >
            <Download className="size-3.5" />
          </a>
        </li>
      ))}
    </ul>
  );
}

interface MessageCardProps {
  message: MessageView;
  accountEmail: string;
  expanded: boolean;
  now: number;
  onToggle: (key: string) => void;
}

export const MessageCard = memo(function MessageCard({
  message,
  accountEmail,
  expanded,
  now,
  onToggle,
}: MessageCardProps) {
  const from = addressLabel(message.from, accountEmail);
  const time = (
    <time
      dateTime={message.internalDate}
      title={formatFull(message.internalDate)}
      className="shrink-0 text-sm text-muted tabular-nums"
    >
      {formatListTime(message.internalDate, now)}
    </time>
  );

  if (!expanded) {
    return (
      <article className="rounded-xl bg-paper ring-1 ring-line">
        <button
          type="button"
          onClick={() => onToggle(message.key)}
          aria-expanded={false}
          className="flex w-full items-baseline gap-3 rounded-xl px-4 py-3 text-left hover:bg-sunk/40 md:px-5"
        >
          <span
            className={cn(
              "shrink-0",
              message.isUnread ? "font-semibold" : "font-medium text-ink-2",
            )}
          >
            {from}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-muted">{message.snippet}</span>
          {time}
        </button>
      </article>
    );
  }

  const recipients = [...message.to, ...message.cc];
  return (
    <article className="rounded-xl bg-paper ring-1 ring-line">
      <header className="flex items-start gap-3 px-4 pt-4 md:px-5">
        <button
          type="button"
          onClick={() => onToggle(message.key)}
          aria-expanded
          className="min-w-0 flex-1 text-left"
        >
          <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-ink">{from}</span>
            {message.from && from !== message.from.email ? (
              <span className="truncate text-sm text-muted">{message.from.email}</span>
            ) : null}
            {message.isDraft ? (
              <span className="rounded border border-line px-1.5 text-xs text-muted">Draft</span>
            ) : null}
          </span>
          {recipients.length ? (
            <span
              className="block truncate text-sm text-muted"
              title={recipients.map(formatAddress).join(", ")}
            >
              to {participantsLabel(message.to, accountEmail, 4)}
              {message.cc.length ? `, cc ${participantsLabel(message.cc, accountEmail, 3)}` : ""}
            </span>
          ) : null}
        </button>
        {time}
      </header>
      <div className="px-4 pt-3 pb-4 md:px-5">
        <MessageBody message={message} />
      </div>
      <Attachments message={message} />
    </article>
  );
});
